import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
	GoogleMap,
	DirectionsService,
	DirectionsRenderer,
	useLoadScript,
	OverlayViewF,
	PolylineF,
} from '@react-google-maps/api';
import { Modal } from 'antd';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import { getSignedUrl } from 'store/commonSlice';
import { getTripMapPathV2 } from 'store/tripSliceV2';
import VehicleTooltip from './VehicleTooltip';
import { NODE_STATUS, normalizeNodeStatus } from 'utils/tripNodeStatus';

const COLOR_TRAVELLED = '#00C853';
const COLOR_REMAINING = '#4ca2d4ff';
const COLOR_TRAIL_GAP = '#BDBDBD';
const COLOR_IN_PROGRESS = '#FF9800';
const COLOR_PENDING = '#E0E0E0';
const COLOR_VEHICLE = '#03A9F4';
const COLOR_MUTED = '#757575';

/** How often the travelled trail is refreshed. The GPS feed is coarser than this. */
const MAP_PATH_POLL_MS = 30000;

/**
 * A jump longer than this between consecutive fixes is a coverage gap, not a drive. We have
 * no idea what route was taken across it, so it is drawn as a dashed grey "no data" hop
 * instead of being claimed as tracked movement.
 */
const TRAIL_GAP_MINUTES = 30;

/**
 * Directions accepts at most 25 waypoints, so a long trail is evenly downsampled to this
 * many fixes before being road-snapped. The recorded fixes are always the anchors — only
 * the road geometry between them is Google's.
 */
const MAX_ROUTE_POINTS = 25;

/** Rough geographic centre of India — only used before any coordinates are available. */
const FALLBACK_CENTER = { lat: 20.5937, lng: 78.9629 };

const containerStyle = {
	width: '100%',
	height: '400px',
};

const DASHED_LINE_ICONS = [
	{
		icon: {
			path: 'M 0,-1 0,1',
			strokeOpacity: 1,
			scale: 4,
		},
		offset: '0',
		repeat: '20px',
	},
];

const minutesBetween = (from, to) => {
	if (!from || !to) return 0;

	const start = moment.utc(from);
	const end = moment.utc(to);
	if (!start.isValid() || !end.isValid()) return 0;

	return Math.abs(end.diff(start, 'minutes'));
};

/**
 * Split the travelled trail into continuous runs and the coverage gaps between them.
 * Runs are drawn solid green; each gap becomes one two-point dashed grey segment.
 */
function splitTrailIntoSegments(points) {
	const runs = [];
	const gaps = [];
	let run = [];

	points.forEach((point, index) => {
		if (index === 0) {
			run = [point];
			return;
		}

		const previous = points[index - 1];
		if (minutesBetween(previous.time, point.time) > TRAIL_GAP_MINUTES) {
			if (run.length >= 2) runs.push(run);
			gaps.push([previous, point]);
			run = [point];
			return;
		}

		run.push(point);
	});

	if (run.length >= 2) runs.push(run);

	return { runs, gaps };
}

/** Evenly thin `points` to at most `max` entries, always keeping the first and last. */
function downsample(points, max) {
	if (points.length <= max) return points;

	const stride = (points.length - 1) / (max - 1);
	const out = [];
	for (let i = 0; i < max; i += 1) out.push(points[Math.round(i * stride)]);

	return out;
}

function nodePinColor(status) {
	if (status === NODE_STATUS.COMPLETED) return COLOR_TRAVELLED;
	if (status === NODE_STATUS.IN_PROGRESS) return COLOR_IN_PROGRESS;
	return COLOR_PENDING;
}

/**
 * Trip map.
 *
 * The green line is the vehicle's ACTUAL travelled path, from
 * `search_type: trip_map_path` — not a predicted route. Only the remaining leg is predicted,
 * via a single cached Directions request from the vehicle through the not-yet-completed
 * nodes.
 */
function MapView({ tripId, trackingInfo, liveTracking, tripInfo }) {
	const { isLoaded } = useLoadScript({
		googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_KEY,
	});

	const dispatch = useDispatch();
	const [map, setMap] = useState(null);
	const [mapPath, setMapPath] = useState(null);
	const [pathLoading, setPathLoading] = useState(true);
	const [travelledLegs, setTravelledLegs] = useState([]);

	const [travelledRouteFailed, setTravelledRouteFailed] = useState(false);
	const [remainingRoute, setRemainingRoute] = useState(null);
	const [remainingRouteFailed, setRemainingRouteFailed] = useState(false);
	const [hoveredVehicle, setHoveredVehicle] = useState(false);
	const [vehicleAddress, setVehicleAddress] = useState('-');
	const [imageModalVisible, setImageModalVisible] = useState(false);
	const [modalImage, setModalImage] = useState('');

	const hoverTimeout = useRef(null);
	const geocodeCache = useRef({ key: null, value: '-' });
	const hasFitBounds = useRef(false);
	const initialCenter = useRef(null);

	// ---------------------------------------------------------------- trail polling
	// Lives here rather than on the page so the trail is only fetched while the Map View
	// tab is actually open.
	useEffect(() => {
		if (!tripId) return undefined;

		let cancelled = false;

		const fetchPath = () =>
			dispatch(getTripMapPathV2({ trip_id: tripId }))
				.then((res) => {
					if (!cancelled) setMapPath(res);
				})
				.catch((err) => {
					// Keep the last good trail on a transient failure rather than blanking it.
					console.error('Failed to fetch trip map path', err);
				})
				.finally(() => {
					if (!cancelled) setPathLoading(false);
				});

		fetchPath();
		const intervalId = setInterval(fetchPath, MAP_PATH_POLL_MS);

		return () => {
			cancelled = true;
			clearInterval(intervalId);
		};
	}, [dispatch, tripId]);

	useEffect(() => {
		hasFitBounds.current = false;
		initialCenter.current = null;
	}, [tripId]);

	// ---------------------------------------------------------------- derived data
	// `nodes` carries geofence_radius_m and arrival/departure times that `tracking_nodes`
	// does not, so it is preferred; both are the same coordinates.
	const nodes = useMemo(() => {
		const source = liveTracking?.nodes?.length ? liveTracking.nodes : trackingInfo;

		return (Array.isArray(source) ? source : [])
			.map((node, index) => {
				const lat = Number(node?.latitude);
				const lng = Number(node?.longitude);
				if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

				return {
					...node,
					lat,
					lng,
					// The node's own status, not the trip's. A closed trip used to force
					// every node to Completed here regardless of what the API sent, so a
					// node the backend never actually completed still rendered green -
					// the map could not disagree with the trip status even when the data did.
					status: normalizeNodeStatus(node.status),
					label: node.node_name || node.name || `Stop ${index + 1}`,
				};
			})
			.filter(Boolean);
	}, [liveTracking?.nodes, trackingInfo]);

	const travelled = useMemo(() => {
		const points = mapPath?.path || [];
		const current = mapPath?.currentLocation;
		if (!current) return points;

		// current_location is usually newer than the last archived fix; append it so the
		// green line actually reaches the truck instead of stopping short of it.
		const last = points[points.length - 1];
		if (last && last.lat === current.lat && last.lng === current.lng) return points;

		return [...points, current];
	}, [mapPath]);

	// Straight-line form of the trail. Drawn immediately, and kept as the fallback if the
	// road-snapped version cannot be built.
	const { runs, gaps } = useMemo(() => splitTrailIntoSegments(travelled), [travelled]);

	// Road-snapped trail. The recorded fixes are fed to Directions as waypoints, which
	// returns one leg per consecutive pair — so each leg can be styled by whether that pair
	// was continuously tracked or spans a coverage gap, off a single request.
	//
	// Note the road geometry BETWEEN two fixes is Google's best guess, not observed data.
	// That is why gap legs stay dashed: the endpoints are real, the line between them is not.
	const routePoints = useMemo(() => downsample(travelled, MAX_ROUTE_POINTS), [travelled]);

	const travelledRouteKey =
		routePoints.length >= 2
			? `${routePoints.length}:${routePoints[0].time}:${
					routePoints[routePoints.length - 1].time
			  }`
			: null;

	// Frozen against travelledRouteKey: the gap flags must describe the same point list that
	// produced the legs, or leg styling would be mismatched by an in-flight poll.
	const travelledRoute = useMemo(() => {
		if (!travelledRouteKey) return null;

		const last = routePoints[routePoints.length - 1];

		return {
			request: {
				origin: { lat: routePoints[0].lat, lng: routePoints[0].lng },
				destination: { lat: last.lat, lng: last.lng },
				waypoints: routePoints.slice(1, -1).map((point) => ({
					location: { lat: point.lat, lng: point.lng },
					stopover: true,
				})),
				travelMode: 'DRIVING',
			},
			gapFlags: routePoints
				.slice(1)
				.map(
					(point, index) =>
						minutesBetween(routePoints[index].time, point.time) > TRAIL_GAP_MINUTES
				),
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [travelledRouteKey]);

	useEffect(() => {
		setTravelledLegs([]);
		setTravelledRouteFailed(false);
	}, [travelledRouteKey]);

	const vehicle = useMemo(() => {
		if (mapPath?.currentLocation) return mapPath.currentLocation;

		// Trip has ended: park the marker on the last known fix.
		const lastPoint = travelled[travelled.length - 1];
		if (lastPoint) return lastPoint;

		const fallback = liveTracking?.current_location;
		const lat = Number(fallback?.latitude);
		const lng = Number(fallback?.longitude);
		if (Number.isFinite(lat) && Number.isFinite(lng)) {
			return { lat, lng, time: fallback?.timestamp || null };
		}

		return null;
	}, [mapPath, travelled, liveTracking?.current_location]);

	const pendingNodes = useMemo(
		() => nodes.filter((node) => node.status !== NODE_STATUS.COMPLETED),
		[nodes]
	);

	// A single-node round trip lists the same physical stop twice at identical coordinates.
	// Stack such pins vertically so the second one is not hidden under the first.
	const pins = useMemo(() => {
		const seen = new Map();

		return nodes.map((node) => {
			const key = `${node.lat.toFixed(5)},${node.lng.toFixed(5)}`;
			const stackIndex = seen.get(key) || 0;
			seen.set(key, stackIndex + 1);
			return { ...node, stackIndex };
		});
	}, [nodes]);

	/** The node the vehicle is heading for — what the tooltip describes. */
	const activeNode = pendingNodes[0] || nodes[nodes.length - 1] || null;

	// Refetch the remaining route only when the pending stops change, or the vehicle has
	// moved far enough to matter (3dp of latitude is roughly 110 m). Without this the route
	// was refetched on every poll, costing two Directions calls every 15 seconds.
	const remainingRouteKey = useMemo(() => {
		if (!pendingNodes.length) return null;

		const stops = pendingNodes
			.map((node) => `${node.sequence_order}:${node.node_id}`)
			.join('|');
		const origin = vehicle || pendingNodes[0];

		return `${stops}@${origin.lat.toFixed(3)},${origin.lng.toFixed(3)}`;
	}, [pendingNodes, vehicle]);

	// Frozen against remainingRouteKey on purpose: the request must not change identity
	// between the key changing and the Directions response arriving.
	const remainingRouteRequest = useMemo(() => {
		if (!remainingRouteKey) return null;

		// With no GPS at all, show the whole planned route from the first stop onwards.
		const origin = vehicle
			? { lat: vehicle.lat, lng: vehicle.lng }
			: { lat: pendingNodes[0].lat, lng: pendingNodes[0].lng };
		const stops = vehicle ? pendingNodes : pendingNodes.slice(1);
		if (!stops.length) return null;

		const destination = stops[stops.length - 1];

		return {
			origin,
			destination: { lat: destination.lat, lng: destination.lng },
			waypoints: stops
				.slice(0, -1)
				.map((node) => ({ location: { lat: node.lat, lng: node.lng } })),
			travelMode: 'DRIVING',
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [remainingRouteKey]);

	useEffect(() => {
		setRemainingRoute(null);
		setRemainingRouteFailed(false);
	}, [remainingRouteKey]);

	/** Straight-line stand-in used when Directions cannot serve the route. */
	const remainingFallbackPath = useMemo(() => {
		if (!remainingRouteRequest) return [];

		return [
			remainingRouteRequest.origin,
			...remainingRouteRequest.waypoints.map((waypoint) => waypoint.location),
			remainingRouteRequest.destination,
		];
	}, [remainingRouteRequest]);

	// ---------------------------------------------------------------- viewport
	if (!initialCenter.current) {
		const seed = vehicle || nodes[0] || travelled[0];
		if (seed) initialCenter.current = { lat: seed.lat, lng: seed.lng };
	}

	useEffect(() => {
		// Wait for the first trail response: nodes alone can bound a much smaller area than
		// the trail, and fitting before it lands would leave the path off-screen for good.
		if (!map || !window.google || pathLoading || hasFitBounds.current) return;

		const points = [
			...nodes.map((node) => ({ lat: node.lat, lng: node.lng })),
			...travelled.map((point) => ({ lat: point.lat, lng: point.lng })),
		];
		if (vehicle) points.push({ lat: vehicle.lat, lng: vehicle.lng });
		if (!points.length) return;

		const bounds = new window.google.maps.LatLngBounds();
		points.forEach((point) => bounds.extend(point));

		// A single distinct coordinate makes fitBounds zoom to maximum.
		if (bounds.getNorthEast().equals(bounds.getSouthWest())) {
			map.setCenter(points[0]);
			map.setZoom(14);
		} else {
			map.fitBounds(bounds);
		}

		// Fit once. Refitting on every poll would yank the map away from a user who panned.
		hasFitBounds.current = true;
	}, [map, pathLoading, nodes, travelled, vehicle]);

	// ---------------------------------------------------------------- reverse geocoding
	// Only while the tooltip is open — the address is not visible otherwise, and this used
	// to fire on every position change.
	useEffect(() => {
		if (!isLoaded || !hoveredVehicle || !vehicle || !window.google) return undefined;

		const key = `${vehicle.lat.toFixed(4)},${vehicle.lng.toFixed(4)}`;
		if (geocodeCache.current.key === key) {
			setVehicleAddress(geocodeCache.current.value);
			return undefined;
		}

		let cancelled = false;
		new window.google.maps.Geocoder().geocode(
			{ location: { lat: vehicle.lat, lng: vehicle.lng } },
			(results, status) => {
				if (cancelled) return;
				const value = status === 'OK' && results?.[0] ? results[0].formatted_address : '-';
				geocodeCache.current = { key, value };
				setVehicleAddress(value);
			}
		);

		return () => {
			cancelled = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isLoaded, hoveredVehicle, vehicle?.lat, vehicle?.lng]);

	// ---------------------------------------------------------------- handlers
	const openPodImage = async (fileUrl, type) => {
		if (!fileUrl) return;

		try {
			let entityName = '';
			if (type === 'pod') entityName = 'pod_image';
			if (type === 'odometer') entityName = 'trip';

			const fileKey =
				typeof fileUrl === 'string' ? fileUrl.split('/').pop().split('?')[0] : fileUrl;

			const res = await dispatch(
				getSignedUrl({
					url_type: 'view',
					data: {
						entity: entityName,
						keys: [fileKey],
					},
				})
			);
			const signedUrl = res?.data?.[0]?.url;
			if (!signedUrl) return;

			setImageModalVisible(true);
			setModalImage(signedUrl);
		} catch (err) {
			console.log(err);
		}
	};

	// ---------------------------------------------------------------- render
	if (!isLoaded) return <div>Loading Map...</div>;

	if (!nodes.length) {
		return (
			<div style={{ padding: 20, textAlign: 'center' }}>
				No route data available for the map.
			</div>
		);
	}

	const vehicleNumber =
		liveTracking?.current_location?.vehicle_number ||
		liveTracking?.vehicle_info?.vehicle_number ||
		'-';
	const showNoGpsNotice = !pathLoading && !travelled.length;

	return (
		<div style={{ position: 'relative', width: '100%', height: '100%' }}>
			<GoogleMap
				mapContainerStyle={containerStyle}
				center={initialCenter.current || FALLBACK_CENTER}
				zoom={12}
				onLoad={setMap}
				options={{
					disableDefaultUI: true,
					fullscreenControl: true,
				}}
			>
				{/* Road-snap the recorded fixes. One leg comes back per consecutive pair. */}
				{travelledRoute && !travelledLegs.length && !travelledRouteFailed && (
					<DirectionsService
						options={travelledRoute.request}
						callback={(result, status) => {
							const legs = result?.routes?.[0]?.legs || [];
							if (status !== 'OK' || !legs.length) {
								// Falls back to the straight-line trail below, which is a
								// truthful rendering of the same fixes.
								console.warn('Travelled path road-snap failed:', status);
								setTravelledRouteFailed(true);
								return;
							}

							setTravelledLegs(
								legs.map((leg, index) => ({
									path: (leg.steps || []).flatMap(
										(step) => step.path || step.lat_lngs || []
									),
									isGap: !!travelledRoute.gapFlags[index],
								}))
							);
						}}
					/>
				)}

				{/* Road-following trail: solid green where tracked, dashed grey across gaps. */}
				{travelledLegs.map((leg, index) => (
					<PolylineF
						// eslint-disable-next-line react/no-array-index-key
						key={`leg-${travelledRouteKey}-${index}`}
						path={leg.path}
						options={{
							strokeColor: leg.isGap ? COLOR_TRAIL_GAP : COLOR_TRAVELLED,
							strokeWeight: leg.isGap ? 3 : 5,
							strokeOpacity: leg.isGap ? 0 : 1,
							geodesic: true,
							clickable: false,
							zIndex: leg.isGap ? 1 : 2,
							...(leg.isGap ? { icons: DASHED_LINE_ICONS } : {}),
						}}
					/>
				))}

				{/* Straight-line trail: shown until the road-snap lands, and if it fails. */}
				{!travelledLegs.length && (
					<>
						{gaps.map((gap) => (
							<PolylineF
								key={`gap-${gap[0].time}-${gap[0].lat},${gap[0].lng}`}
								path={gap}
								options={{
									strokeColor: COLOR_TRAIL_GAP,
									strokeWeight: 3,
									strokeOpacity: 0,
									geodesic: true,
									clickable: false,
									zIndex: 1,
									icons: DASHED_LINE_ICONS,
								}}
							/>
						))}
						{runs.map((run) => (
							<PolylineF
								key={`run-${run[0].time}-${run[0].lat},${run[0].lng}`}
								path={run}
								options={{
									strokeColor: COLOR_TRAVELLED,
									strokeWeight: 5,
									strokeOpacity: 1,
									geodesic: true,
									clickable: false,
									zIndex: 2,
								}}
							/>
						))}
					</>
				)}

				{/* Remaining leg — the only predicted geometry on the map. */}
				{remainingRouteRequest && !remainingRoute && !remainingRouteFailed && (
					<DirectionsService
						options={remainingRouteRequest}
						callback={(result, status) => {
							if (status === 'OK') {
								setRemainingRoute(result);
								return;
							}
							// Long routes exceed the waypoint cap; fall back to straight
							// lines rather than dropping the remaining leg silently.
							console.warn('Remaining route Directions request failed:', status);
							setRemainingRouteFailed(true);
						}}
					/>
				)}
				{remainingRoute && (
					<DirectionsRenderer
						options={{
							directions: remainingRoute,
							suppressMarkers: true,
							preserveViewport: true,
							polylineOptions: {
								strokeColor: COLOR_REMAINING,
								strokeWeight: 4,
								strokeOpacity: 0,
								zIndex: 3,
								icons: DASHED_LINE_ICONS,
							},
						}}
					/>
				)}
				{remainingRouteFailed && remainingFallbackPath.length >= 2 && (
					<PolylineF
						path={remainingFallbackPath}
						options={{
							strokeColor: COLOR_REMAINING,
							strokeWeight: 4,
							strokeOpacity: 0,
							geodesic: true,
							clickable: false,
							zIndex: 3,
							icons: DASHED_LINE_ICONS,
						}}
					/>
				)}

				{vehicle && (
					<OverlayViewF position={vehicle} mapPaneName="overlayMouseTarget">
						<div
							style={{
								position: 'relative',
								marginLeft: '-22px',
								marginTop: '-22px',
								cursor: 'pointer',
								pointerEvents: 'auto',
							}}
							onMouseEnter={() => {
								clearTimeout(hoverTimeout.current);
								setHoveredVehicle(true);
							}}
							onMouseLeave={() => {
								hoverTimeout.current = setTimeout(
									() => setHoveredVehicle(false),
									200
								);
							}}
						>
							{hoveredVehicle && (
								<div
									style={{
										position: 'absolute',
										top: '20%',
										left: '50%',
										transform: 'translate(-50%, -120%)',
										paddingBottom: '15px',
										zIndex: 9999,
										pointerEvents: 'auto',
										cursor: 'default',
									}}
								>
									<VehicleTooltip
										vehicleNumber={vehicleNumber}
										node={activeNode}
										address={vehicleAddress}
										time={vehicle.time}
										podRequired={!!tripInfo?.pod_required}
										onOpenImage={openPodImage}
									/>
								</div>
							)}
							<div
								style={{
									width: 45,
									height: 45,
									borderRadius: '50%',
									background: COLOR_VEHICLE,
									boxShadow: '0 0 0 8px rgba(3, 169, 244, 0.3)',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
								}}
							>
								<img
									src="/icons/blue-truck.png"
									width="24"
									height="24"
									alt="truck"
									style={{ filter: 'brightness(0) invert(1)' }}
								/>
							</div>
						</div>
					</OverlayViewF>
				)}

				{pins.map((node, index) => {
					const isPending =
						node.status !== NODE_STATUS.COMPLETED &&
						node.status !== NODE_STATUS.IN_PROGRESS;

					return (
						<OverlayViewF
							key={`${node.sequence_order ?? index}-${node.node_id ?? index}`}
							position={{ lat: node.lat, lng: node.lng }}
							mapPaneName="overlayMouseTarget"
						>
							<div
								style={{
									position: 'absolute',
									transform: `translate(-50%, calc(-100% - ${
										node.stackIndex * 46
									}px))`,
									display: 'flex',
									flexDirection: 'column',
									alignItems: 'center',
									pointerEvents: 'none',
								}}
							>
								<div
									style={{
										width: 32,
										height: 32,
										borderRadius: '50%',
										background: nodePinColor(node.status),
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										color: isPending ? COLOR_MUTED : 'white',
										fontWeight: 'bold',
										fontSize: 16,
										boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
										marginBottom: 6,
										border: isPending ? '2px solid white' : 'none',
									}}
								>
									{isPending ? index + 1 : '✓'}
								</div>
								<div
									style={{
										background: 'white',
										padding: '4px 10px',
										borderRadius: 12,
										fontSize: 12,
										fontWeight: 500,
										boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
										whiteSpace: 'nowrap',
										color: '#333',
									}}
								>
									{node.label}
								</div>
							</div>
						</OverlayViewF>
					);
				})}
			</GoogleMap>

			{showNoGpsNotice && (
				<div
					style={{
						position: 'absolute',
						top: 12,
						left: 12,
						background: 'rgba(255,255,255,0.95)',
						padding: '6px 12px',
						borderRadius: 6,
						fontSize: 12,
						fontWeight: 600,
						color: COLOR_MUTED,
						boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
					}}
				>
					No GPS data received for this trip yet
				</div>
			)}

			<Modal
				open={imageModalVisible}
				footer={null}
				onCancel={() => setImageModalVisible(false)}
				centered
			>
				<img
					key={modalImage}
					src={modalImage}
					alt="POD"
					style={{ width: '100%', borderRadius: '8px' }}
				/>
			</Modal>
		</div>
	);
}

MapView.propTypes = {
	tripId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	trackingInfo: PropTypes.array,
	liveTracking: PropTypes.object,
	tripInfo: PropTypes.object,
};

export default MapView;
